import { motion } from "framer-motion";
import { EyeOff, Shuffle, TrendingDown, AlertTriangle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const challenges = [
  {
    icon: EyeOff,
    title: "Isolated guest history",
    description:
      "Your platform sees its own bookings. Not the five others the same guest used.",
  },
  {
    icon: Shuffle,
    title: "Guests relocate, patterns don't",
    description:
      "Repeat no-shows rarely stay on one platform. By the time a restaurant raises it, the guest is booking elsewhere — often on you.",
  },
  {
    icon: TrendingDown,
    title: "Restaurant churn signal",
    description:
      "Restaurants don't invoice empty tables. They renegotiate, downgrade, or leave. By the time retention data shows it, the decision is months old.",
  },
];

const Problem = () => {
  return (
    <section id="problem" className="relative py-16 overflow-hidden">
      <div className="container relative mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-destructive/10 px-4 py-1.5 text-sm font-medium text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Diagnosis
          </div>
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            The guest left your platform.
            <br />
            <span className="text-primary">Your guest history stayed behind.</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Nine in ten restaurants name no-shows as their top priority. For a platform, that's not a complaint — it's the leading indicator of churn.
          </p>
        </motion.div>

        {/* Mobile: accordion */}
        <Accordion
          type="single"
          collapsible
          defaultValue={challenges[0].title}
          className="md:hidden space-y-3 max-w-4xl mx-auto"
        >
          {challenges.map((challenge) => (
            <AccordionItem
              key={challenge.title}
              value={challenge.title}
              className="rounded-xl border border-border bg-card px-4"
            >
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 flex-1 text-left">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-destructive/10 text-destructive shrink-0">
                    <challenge.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-bold text-foreground leading-tight">
                    {challenge.title}
                  </h3>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground pb-4">
                  {challenge.description}
                </p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Desktop: grid */}
        <div className="hidden md:grid grid-cols-1 gap-8 md:grid-cols-3 max-w-4xl mx-auto">
          {challenges.map((challenge, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="text-center"
            >
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                <challenge.icon className="h-6 w-6" />
              </div>
              <h3 className="mb-2 text-base font-bold text-foreground md:whitespace-nowrap">
                {challenge.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {challenge.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Pull quote */}
        <motion.blockquote
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-14 max-w-2xl mx-auto border-l-2 border-primary/40 pl-6"
        >
          <p className="text-muted-foreground italic leading-relaxed">
            "The quality of your guests is too low. We're paying commission on covers that don't show up."
          </p>
          <footer className="mt-3 text-xs text-muted-foreground/70">
            — Restaurant, anonymised feedback to a booking platform, 2024
          </footer>
        </motion.blockquote>
      </div>
    </section>
  );
};

export default Problem;
